import { action, computed, observable } from 'mobx';
import axios from 'axios';
import { History } from 'history';
import ROUTES, {
  AUTH_ROUTES,
  IRouteOptions
} from '@app/src/common/constants/routeUrls';
import AuthStore from '@app/src/common/store/AuthStore';

export interface ILocation {
  hash: string;
  pathname: string;
  search: string;
  state: string;
}

export interface IMatch<T> {
  isExact: boolean;
  path: string;
  url: string;
  params: T;
}

type Target = '_blanc' | '_self';

const CONFIG_PATH = '/assets/json/config.json';
const BE_AUTH_PATH = '/api/user-management/auth/auth';

export class RouterStore {
  @computed
  get routesLength(): number {
    return this.routes.length;
  }

  @computed
  get firstRoutePath(): string {
    return this.targetPath
      ? this.targetPath
      : this.routes.length
        ? this.routes[0].path
        : ROUTES.authPage.path;
  }

  static staticUrl(url: string): string {
    const pathname = window.location.origin;
    const pathnameWithoutSlash = pathname.endsWith('/')
      ? pathname.slice(0, -1)
      : pathname;
    return pathnameWithoutSlash + url;
  }

  @observable.shallow routes: IRouteOptions[] = [];

  @observable location: ILocation;
  @observable match: IMatch<any>;
  @observable history: History;
  @observable targetPath: string | null;

  private composerUrl: string;
  private wayFindingBuilderUrl: string;
  private onlineRegistrationUrl: string;
  private formBuilderUrl: string;
  private truckManagementUrl: string;
  state: any = undefined;

  constructor() {
    this.fetchComponentUrls();
  }

  setState = (state: any) => {
    this.state = state;
  };

  @action
  fetchComponentUrls = () => {
    axios
      .get(RouterStore.staticUrl(CONFIG_PATH))
      .then(resp => resp.data)
      .then(data => {
        this.composerUrl = data.COMPOSER_URL;
        this.wayFindingBuilderUrl = data.WAY_FINDING_BUILDER_URL;
        this.onlineRegistrationUrl = data.ONLINE_REGISTRATION_URL;
        this.formBuilderUrl = data.FORM_BUILDER_URL;
        this.truckManagementUrl = data.TRUCK_MANAGEMENT_URL;
      });
  };

  @action
  updateRoutes(routes: IRouteOptions[]): void {
    this.routes = routes;
  }

  @action
  isExact(path: string): boolean {
    return this.location.pathname === path;
  }

  @action
  selectRoute(location: ILocation, match: IMatch<any>, history: History): void {
    this.location = location;
    this.match = match;
    this.history = history;
    if (!this.isAuthorizedRoute(location)) {
      return;
    }
    const routeStartsWith = this.routes.find(r =>
      location.pathname.startsWith(r.path)
    );
    const routeEndWith = this.routes.find(r =>
      location.pathname.endsWith(r.path)
    );
    // Check that user is authorized to go to specified module
    if (this.routes.length && !routeStartsWith && !routeEndWith) {
      this.navigate(ROUTES.notAuthorizedPage.path);
    }
    // Check if user should accept terms and conditions
    if (AuthStore.user?.consentFileUrl) {
      this.navigate(ROUTES.consentPage.path);
    }
  }

  @action
  navigate(path: string, state?: any, refresh?: boolean): void {
    if (this.history) {
      this.state = state;
      if (!this.isExact(path)) {
        // to fix warning: Hash history cannot PUSH the same path; a new entry will not be added to the history
        this.history.push(path);
      } else if (refresh) {
        this.history.go(0);
      }
    } else {
      window.location.href = `/#${path}`;
    }
  }

  @action
  navigateOrRefresh(path: string, state?: any): void {
    this.navigate(path, state, true);
  }

  gotoAuthentication() {
    window.location.href = BE_AUTH_PATH;
  }

  @action
  clearRoutes(): void {
    this.routes = [];
    this.targetPath = null;
  }

  isAuthorizedRoute(location: ILocation): boolean {
    return Object.values(AUTH_ROUTES).some(route =>
      location.pathname.startsWith(route.path)
    );
  }

  get basePath() {
    return window.location.href.split('#').slice(0, 1).join();
  }

  navigateToComposer(projectId: string, target: Target = '_self') {
    AuthStore.checkAccess().then(response => {
      if (this.composerUrl && response && response.token) {
        window.open(`${this.basePath + this.composerUrl}/${projectId}`, target);
      }
    });
  }

  navigateToEditDocument(visitorId: string, documentId: string, lang: string) {
    AuthStore.checkAccess().then(response => {
      if (this.onlineRegistrationUrl && response && response.token) {
        window.open(
          `${this.basePath + this.onlineRegistrationUrl}/document-edit?visitorId=${visitorId}&documentId=${documentId}&lang=${lang}`,
          '_blank'
        );
      }
    });
  }

  navigateToWayFindingBuilder(wayFinding: string) {
    AuthStore.checkAccess().then(response => {
      if (this.wayFindingBuilderUrl && response && response.token) {
        window.location.href = `${this.basePath + this.wayFindingBuilderUrl}/${wayFinding}`;
      }
    });
  }

  navigateToFormBuilder(formId: string, target: Target = '_self') {
    AuthStore.checkAccess().then(response => {
      if (this.composerUrl && response && response.token) {
        window.open(`${this.basePath + this.formBuilderUrl}/${formId}`, target);
      }
    });
  }

  // Truck Management is a separate frontend app that shares the platform session. The sidebar and
  // the Module Selector hand the browser over to it in the same tab; checkAccess refreshes the token
  // first so the destination app starts with a valid session.
  navigateToTruckManagement() {
    AuthStore.checkAccess()
      .then(response => {
        if (this.truckManagementUrl && response && response.token) {
          window.location.href = `${this.basePath + this.truckManagementUrl}/`;
        }
      })
      // checkAccess rejects when the session is gone; it already drives the redirect to the login
      // page, so swallow the rejection instead of leaving it unhandled.
      .catch(() => null);
  }

  openInNewWindow(url: string) {
    window.open(`${this.basePath}#${url}`, '_blank');
  }
}

export default new RouterStore();
